import React from "react";
import {
  collectHouseholdAddresses,
  addressMatchKey,
  ADDRESS_IDENTITY_KEYS,
} from "../utils/householdAddresses";

const DEFAULT_SELECT_CLASS =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500";

/** Dropdown of addresses already entered in the household; selecting one pre-fills the caller's fields.
 *
 * Adapter API — every surface wires it the same way:
 *   getField(key)        -> current value of an address field
 *   setField(key, value) -> write an address field
 *   onAfterSelect()      -> optional; runs once after a fill (e.g. to remount StateCitySelect)
 *
 * Controlled: reflects the currently-entered address as the selected option and reverts to the
 * placeholder as soon as the user edits a field so it no longer matches. */
const ExistingAddressPicker = ({
  household,
  excludeMemberId,
  getField,
  setField,
  onAfterSelect,
  label = "Use an existing address",
  className = "",
  selectClassName = DEFAULT_SELECT_CLASS,
}) => {
  const options = collectHouseholdAddresses(household, { excludeMemberId });
  if (!options.length) return null;

  const currentAddress = {};
  ADDRESS_IDENTITY_KEYS.forEach((key) => {
    currentAddress[key] = getField(key);
  });
  const currentKey = addressMatchKey(currentAddress);
  const selectedIndex = options.findIndex((option) => option.key === currentKey);
  const value = selectedIndex >= 0 ? String(selectedIndex) : "";

  const handleChange = (event) => {
    const index = event.target.value;
    if (index === "") return;
    const selected = options[Number(index)];
    if (!selected) return;
    ADDRESS_IDENTITY_KEYS.forEach((key) => setField(key, selected.address[key] || ""));
    if (onAfterSelect) onAfterSelect();
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select className={selectClassName} value={value} onChange={handleChange}>
        <option value="">Select an existing address…</option>
        {options.map((option, index) => (
          <option key={option.key} value={index}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default ExistingAddressPicker;
